<template>
  <div class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header" :class="{ loading: loadingHistory }">
        <h5>Location History</h5>

        <div class="filters-container text-center">
          
          <label for="userType">Role:&nbsp;</label>
          <select id="userType" v-model="filters.userType" class="styled-input" @change="onUserTypeChange">
            <option value="">Select Type</option>
            <option v-for="role in userTypes" :key="role.id" :value="role.name">
              {{ role.name }}
            </option>
          </select>
            

          <label for="userId">User:&nbsp;</label>
          <select id="userId" v-model="filters.userId" class="styled-input" :disabled="!filters.userType || loadingUsers" @change="resetData">
            <option value="">{{ loadingUsers ? 'Loading...' : 'Select User' }}</option>
            <option v-for="user in filteredUsers" :key="user.user_id" :value="user.user_id">
              {{ user.first_name }} {{ user.last_name }}
            </option>
          </select>
            

          <label for="date">Date:&nbsp;</label>
          <input
              type="date"
              id="date"
              v-model="filters.date"
              :max="todayDate"
              class="styled-input"
              @change="resetData" 
          />
            

          <button @click="fetchHistory" class="btn btn-light search-btn" :disabled="loadingHistory">
            <i class="fa-solid fa-magnifying-glass"></i>
            <span class="tooltip-text">Search</span>
          </button>
        </div>

        <button class="btn btn-light close-btn" @click="closeModal">
          <i class="fa-solid fa-xmark"></i>
          <span class="tooltip-text">Close</span>
        </button>
      </div>

      <div class="modal-body">
        <div id="map-container">
          <div v-if="historyData.length === 0 && !loadingHistory && searchedOnce" class="no-data">
            <img src="../../../assets/vectors/data_not_found.png" alt="No Data" class="no-data-image" />
            <h3 class="text-center text-danger">No location history found!</h3>
            <p class="text-muted">No GPS tracking data is available for the selected user and date.</p>
          </div>
          <div v-show="historyData.length > 0 || !searchedOnce" ref="mapContainer" class="map"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMobileAppUserTypes, getUsersByType, getLocationsHistory } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: 'LocationHistoryModal',
  emits: ['close-modal'],

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    const today = new Date().toISOString().split('T')[0];
    return {
      token: localStorage.getItem('token_set'),
      profileId: localStorage.getItem('profile_id'),
      
      loadingHistory: false,
      loadingUsers: false,
      searchedOnce: false,
      
      filters: {
        userType: '',
        userId: '',
        date: today, 
      },
      todayDate: today,

      userTypes: [], 
      filteredUsers: [], 
      historyData: [],
    };
  },

  created() {
    // Non-reactive properties for Google Maps to prevent Vue Proxy issues
    this.map = null;
    this.historyPolyline = null;
    this.historyMarkers = [];
  },

  mounted() {
    this.enterFullScreen();
    this.loadGoogleMaps();
    this.fetchUserTypes(); 
  },

  beforeUnmount() {
    this.exitFullScreen();
    this.clearHistoryMap();
  },

  methods: {
    // Reset map data when filter changes
    resetData() {
      this.clearHistoryMap();
      this.historyData = [];
      this.searchedOnce = false;
    },

    // Fetch user types
    async fetchUserTypes() {
      try {
        const response = await getMobileAppUserTypes(this.token, 3);
        if (response.data && response.data.status === 'success') {
          this.userTypes = response.data.data;
        } else {
          this.toast.error(response.data.message || "Failed to load User Types.");
        }
      } catch (e) {
        console.error(e);
        this.toast.error("An error occurred while fetching user types.");
      }
    },

    // Handle user type change
    async onUserTypeChange() {
      this.filters.userId = '';
      this.filteredUsers = [];
      this.resetData(); // Call reset method

      if (!this.filters.userType) return;

      this.loadingUsers = true;
      try {
        const response = await getUsersByType(this.token, this.profileId, this.filters.userType);
        if (response.data && response.data.status === 'success') {
          this.filteredUsers = response.data.data;
        } else {
          this.toast.error(response.data.message || "Failed to load users for this role.");
        }
      } catch (e) {
        console.error(e);
        this.toast.error("An error occurred while fetching users.");
      } finally {
        this.loadingUsers = false;
      }
    },

    // Fetch history
    async fetchHistory() {
      if (!this.filters.userId) {
        this.toast.error("Please select a User.");
        return;
      }
      if (!this.filters.date) {
        this.toast.error("Please select a Date.");
        return;
      }

      this.loadingHistory = true;
      this.resetData(); // Ensure fresh slate before searching

      try {
        const payload = {
          user_id: parseInt(this.filters.userId),
          date: this.filters.date
        };

        const response = await getLocationsHistory(this.token, payload);

        if (response.data && response.data.status === 'success') {
          this.historyData = response.data.data;
          this.searchedOnce = true;
          // Must await the rendering because fetching road paths is asynchronous
          await this.renderHistoryMap(this.historyData); 
        } else {
          this.toast.error(response.data.message || "Failed to fetch location history.");
          this.searchedOnce = true;
        }
      } catch (e) {
        console.error(e);
        this.toast.error("Failed to fetch location history from server.");
        this.searchedOnce = true;
      } finally {
        this.loadingHistory = false;
      }
    },

    // Enter full screen
    enterFullScreen() {
      const elem = document.documentElement;
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    },

    // Exit full screen
    exitFullScreen() {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    },

    // Close Modal
    closeModal() {
      this.$emit('close-modal');
    },

    // Load Google Maps
    loadGoogleMaps() {
      if (window.google && window.google.maps) {
        this.initMap();
      } else {
        const script = document.createElement("script");
        const googleKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&callback=initMap`;
        script.defer = true;
        script.async = true;
        window.initMap = () => this.initMap();
        document.head.appendChild(script);
      }
    },

    // Initialize Map
    initMap() {
      if (!this.$refs.mapContainer) return;
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: { lat: 7.8731, lng: 80.7718 }, 
        zoom: 7,
        mapTypeControl: false,
        streetViewControl: false,
      });
    },

    // Calculate distance between two points
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371e3; 
      const φ1 = lat1 * Math.PI / 180;
      const φ2 = lat2 * Math.PI / 180;
      const Δφ = (lat2 - lat1) * Math.PI / 180;
      const Δλ = (lon2 - lon1) * Math.PI / 180;

      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c; 
    },

    // Get actual roads via Directions API
    async fetchRoadPath(points) {
      if (points.length < 2) return points;

      const directionsService = new google.maps.DirectionsService();
      const maxWaypoints = 23; // Google Maps allows max 25 points total per request
      let fullPath = [];
      const chunks = [];

      // 1. Break points into overlapping chunks of 25
      for (let i = 0; i < points.length; i += maxWaypoints + 1) {
        let endIdx = i + maxWaypoints + 2;
        if (endIdx > points.length) endIdx = points.length;
        const chunk = points.slice(i, endIdx);
        if (chunk.length > 1) {
          chunks.push(chunk);
        }
      }

      // 2. Fetch routes for each chunk
      for (const chunk of chunks) {
        const origin = chunk[0];
        const destination = chunk[chunk.length - 1];
        
        const waypoints = chunk.slice(1, chunk.length - 1).map(p => ({
          location: p,
          stopover: false
        }));

        try {
          const response = await new Promise((resolve, reject) => {
            directionsService.route({
              origin: origin,
              destination: destination,
              waypoints: waypoints,
              travelMode: google.maps.TravelMode.DRIVING,
            }, (result, status) => {
              if (status === google.maps.DirectionsStatus.OK) {
                resolve(result);
              } else {
                reject(status);
              }
            });
          });

          // Stitch high-resolution road points together
          response.routes[0].legs.forEach(leg => {
            leg.steps.forEach(step => {
              step.path.forEach(p => fullPath.push(p));
            });
          });
        } catch (e) {
          console.warn("Directions API routing failed for a chunk. Falling back to straight lines.", e);
          // Fallback if API rate limits hit: draw straight lines for this chunk
          chunk.forEach(p => fullPath.push(p));
        }
      }
      return fullPath;
    },

    // Draw the Polyline and Markers
    async renderHistoryMap(raw_data) {
      if (!this.map) return;
      
      this.clearHistoryMap();
      if (!raw_data || raw_data.length === 0) return;

      const bounds = new google.maps.LatLngBounds();
      const pathCoordinates = [];

      // 1. Create a SINGLE shared InfoWindow for high performance
      const sharedInfoWindow = new google.maps.InfoWindow({
        disableAutoPan: true
      });

      // 2. Plot all points on the map without calculating distances or filtering out points
      raw_data.forEach((point, index) => {
        if (!point.latitude || !point.longitude) return;

        const position = { 
          lat: parseFloat(point.latitude), 
          lng: parseFloat(point.longitude) 
        };

        pathCoordinates.push(position);
        bounds.extend(position);

        let iconMarker;
        let labelHtml = `<div style="padding: 5px; text-align: center;"><strong>Waypoint</strong><br><small>${point.timestamp}</small></div>`;

        if (index === 0) {
          iconMarker = 'http://maps.google.com/mapfiles/ms/icons/green-dot.png';
          labelHtml = `<div style="padding: 5px; text-align: center;"><strong style="color: green;">🟢 Start</strong><br><small>${point.timestamp}</small></div>`;
        } else if (index === raw_data.length - 1) {
          iconMarker = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
          labelHtml = `<div style="padding: 5px; text-align: center;"><strong style="color: red;">🔴 End</strong><br><small>${point.timestamp}</small></div>`;
        } else {
          iconMarker = {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 3,
            fillColor: '#007bff',
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: '#ffffff'
          };
        }

        const marker = new google.maps.Marker({
          position: position,
          map: this.map,
          icon: iconMarker,
          title: `Time: ${point.timestamp}`,
        });

        // 3. Hover Events using the Shared InfoWindow
        marker.addListener('mouseover', () => {
          sharedInfoWindow.setContent(labelHtml);
          sharedInfoWindow.open(this.map, marker);
        });

        marker.addListener('mouseout', () => {
          sharedInfoWindow.close();
        });

        this.historyMarkers.push(marker);
      });

      // 4. Get the actual roads via Directions API
      const roadPathCoordinates = await this.fetchRoadPath(pathCoordinates);

      // 5. Draw the Waypoint by linking the road nodes
      this.historyPolyline = new google.maps.Polyline({
        path: roadPathCoordinates,
        geodesic: true,
        strokeColor: '#211C84',
        strokeOpacity: 0.8,
        strokeWeight: 4
      });

      this.historyPolyline.setMap(this.map);
      
      // Auto-zoom the map to fit the route
      if (pathCoordinates.length > 0) {
        this.map.fitBounds(bounds); 
      }
    },

    // Clear map before redrawing
    clearHistoryMap() {
      if (this.historyPolyline) {
        this.historyPolyline.setMap(null);
        this.historyPolyline = null;
      }
      if (this.historyMarkers.length > 0) {
        this.historyMarkers.forEach(marker => marker.setMap(null));
        this.historyMarkers = [];
      }
    },
  }
}
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Modal Container */
.modal-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 1200px; 
  max-width: 95%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

/* Filters Container */
.filters-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap; 
  gap: 10px;
}

/* Progress Bar Effect */
.modal-header.loading {
  position: relative;
  border-bottom: none;
}

.modal-header.loading::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 3px;
  background: linear-gradient(to right, #0d6efd, #28a745, #f4b400, #dc3545);
  animation: loadingBar 1.5s linear infinite;
}

@keyframes loadingBar {
  0% { width: 0; }
  50% { width: 50%; }
  100% { width: 100%; }
}

/* Close Button */
.close-btn {
  border-radius: 50%;
  width: 33px;
  height: 33px;
  padding: 0;
  cursor: pointer;
  position: relative;
}

/* Map Container */
#map-container {
  width: 100%;
  height: 550px; 
  background: #f4f4f4;
  border-radius: 8px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.map {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.no-data {
  text-align: center;
  color: #666;
}

.no-data p {
  font-size: 14px;
  font-weight: lighter;
}

.no-data-image {
  max-width: 175px;
  margin-bottom: 10px;
}

/* Reusable Styled Input for Selects and Dates */
.styled-input {
  padding: 5px 10px;
  font-size: 14px;
  border: 1px solid #CCCCCC;
  border-radius: 6px;
  outline: none;
  background-color: #fff;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  height: 33px;
}

.styled-input:hover,
.styled-input:focus {
  border-color: #666;
}

.styled-input::-webkit-calendar-picker-indicator {
  filter: invert(30%);
  cursor: pointer;
}

.styled-input:disabled {
  background-color: #f0f0f0;
  cursor: not-allowed;
  opacity: 0.7;
}

.search-btn {
  border-radius: 5px;
  width: 33px;
  height: 33px;
  padding: 0;
  position: relative;
}

/* Tooltip Styling */
.tooltip-text {
  visibility: hidden;
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 6px 10px;
  border-radius: 4px;
  position: absolute;
  bottom: 40px;
  right: 50%;
  transform: translateX(50%);
  white-space: nowrap;
  font-size: 12px;
  z-index: 1001;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.search-btn:hover .tooltip-text,
.close-btn:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

/* Hide Google Maps InfoWindow close button globally within this component */
:deep(.gm-ui-hover-effect) {
  display: none !important;
}

/* Optional: Remove the default padding from InfoWindow to make it cleaner */
:deep(.gm-style-iw-c) {
  padding: 0 !important;
}
:deep(.gm-style-iw-d) {
  overflow: hidden !important;
}
</style>